import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { FinancialAccount, FinancialAccountSearchCommand } from '../models/financial-account/financial-account';
import { PartyStatement, SubledgerView } from '../models/ledger/subledger';
import { PagedResult } from './bank-account.proxy.service';

@Injectable({ providedIn: 'root' })
export class FinancialAccountProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/financial-accounts';

  /** Cached per company so the chart-of-accounts tree is fetched once even when many selectors mount. */
  private readonly byCompanyCache = new Map<string, Observable<ApiResponse<FinancialAccount[]>>>();

  public search(
    command: FinancialAccountSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    matchAny = false,
  ): Observable<ApiResponse<PagedResult<FinancialAccount>>> {
    const params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('matchAny', matchAny);
    return this.httpClient.post<ApiResponse<PagedResult<FinancialAccount>>>(`${this.baseUrl}/search`, command, { params });
  }

  /** All financial accounts for a company (flat list) — used to build the chart-of-accounts tree.
   * Cached + shared so several account selectors on one page issue a single request. */
  public getByCompany(companyReference: string): Observable<ApiResponse<FinancialAccount[]>> {
    let cached = this.byCompanyCache.get(companyReference);
    if (!cached) {
      cached = this.httpClient.get<ApiResponse<FinancialAccount[]>>(`${this.baseUrl}/company/${companyReference}`).pipe(shareReplay(1));
      this.byCompanyCache.set(companyReference, cached);
    }
    return cached;
  }

  /** Drops the cached chart of accounts (call after creating/editing accounts). */
  public clearCompanyCache(companyReference?: string): void {
    if (companyReference) this.byCompanyCache.delete(companyReference);
    else this.byCompanyCache.clear();
  }

  public getById(id: string): Observable<ApiResponse<FinancialAccount>> {
    return this.httpClient.get<ApiResponse<FinancialAccount>>(`${this.baseUrl}/${id}`);
  }

  /** Create a financial account (Level, NormalBalance and the parent's posting flag are derived server-side). */
  public create(account: FinancialAccount): Observable<ApiResponse<FinancialAccount>> {
    return this.httpClient.post<ApiResponse<FinancialAccount>>(this.baseUrl, account);
  }

  /** Update a financial account. PUT replaces the whole document — send the full account, not a patch. */
  public update(id: string, account: FinancialAccount): Observable<ApiResponse<FinancialAccount>> {
    return this.httpClient.put<ApiResponse<FinancialAccount>>(`${this.baseUrl}/${id}`, account);
  }

  /** Level 2 — a reconciliation account broken down by subledger party, period-to-date (#4713). Pass a
   * fiscalYear + period to view a specific month (through its last day); omit both to use the current period.
   * Parties with an outstanding balance only by default; pass `includeSettled` to also list settled parties. */
  public getSubledger(
    accountId: string,
    fiscalYear?: number | null,
    period?: number | null,
    includeSettled = false,
  ): Observable<ApiResponse<SubledgerView>> {
    let params = new HttpParams();
    if (fiscalYear != null) params = params.set('fiscalYear', fiscalYear);
    if (period != null) params = params.set('period', period);
    if (includeSettled) params = params.set('includeSettled', true);
    return this.httpClient.get<ApiResponse<SubledgerView>>(`${this.baseUrl}/${accountId}/subledger`, { params });
  }

  /** Level 3 — one party's documents on a reconciliation account, period-to-date (#4713). Pass a fiscalYear +
   * period to view a specific month; omit both to use the current period. Open items only by default; pass
   * `includeSettled` to also return fully-paid documents (settled history). */
  public getPartyStatement(
    accountId: string,
    partyId: string,
    fiscalYear?: number | null,
    period?: number | null,
    includeSettled = false,
  ): Observable<ApiResponse<PartyStatement>> {
    let params = new HttpParams();
    if (fiscalYear != null) params = params.set('fiscalYear', fiscalYear);
    if (period != null) params = params.set('period', period);
    if (includeSettled) params = params.set('includeSettled', true);
    return this.httpClient.get<ApiResponse<PartyStatement>>(`${this.baseUrl}/${accountId}/subledger/${partyId}/documents`, { params });
  }
}
